import { Box, BoxProps, useTheme } from '@mui/material';
import { AlertTriangle, Info } from 'lucide-react';
import React, { PropsWithChildren, useCallback } from 'react';

type Props = PropsWithChildren &
  BoxProps & {
    icon?: React.ReactNode;
    variant?: 'info' | 'warning' | 'light';
  };

const getIcon = (variant: 'info' | 'warning' | 'light', style?: React.CSSProperties | undefined) => {
  switch (variant) {
    case 'warning':
      return <AlertTriangle style={style} />;
    case 'info':
      return <Info style={style} />;
    case 'light':
      return <Info style={style} />;
    default:
      return <Info style={style} />;
  }
};

const InfoBox: React.FC<Props> = ({ icon, children, variant = 'info', ...props }) => {
  const theme = useTheme();

  const getBackgroundColor = useCallback(
    (variant: 'info' | 'warning' | 'light') => {
      switch (variant) {
        case 'warning':
          return theme.palette.orange.light;
        case 'info':
          return theme.palette.darkBlue.main;
        case 'light':
          return theme.palette.bluViu.main;
        default:
          return theme.palette.darkBlue.main;
      }
    },
    [variant]
  );

  const getTextColor = useCallback(
    (variant: 'info' | 'warning' | 'light') => {
      switch (variant) {
        case 'warning':
          return theme.palette.darkBlue.main;
        case 'info':
          return theme.palette.common.white;
        default:
          return theme.palette.common.white;
      }
    },
    [variant]
  );

  return (
    <Box
      {...props}
      display={'flex'}
      flexDirection={'row'}
      padding={4}
      gap={4}
      borderRadius={2}
      sx={{
        backgroundColor: getBackgroundColor(variant),
        color: getTextColor(variant),
        alignItems: 'center',
      }}>
      {icon || getIcon(variant)}
      {children}
    </Box>
  );
};

export default React.memo(InfoBox);
